import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@rango/shared/types';
import { api } from '@/lib/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: User | null) => void;
  fetchUser: () => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<void>;
  logout: () => void;
  
  // For development
  mockLogin: (email: string, name: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      error: null,
      
      setUser: (user) => set({ user }),
      
      fetchUser: async () => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await api.getMe();
          set({ user, isLoading: false });
        } catch (error) {
          set({ user: null, isLoading: false });
        }
      },
      
      updateUser: async (data) => {
        const { user } = await api.updateMe(data);
        set({ user });
      },
      
      logout: () => {
        localStorage.removeItem('mock_user_id');
        localStorage.removeItem('mock_email');
        set({ user: null });
      },
      
      // Development only
      mockLogin: async (email, name) => {
        set({ isLoading: true });
        
        // Store mock credentials
        localStorage.setItem('mock_email', email);
        
        try {
          // Try to fetch existing user
          const { user } = await api.getMe();
          localStorage.setItem('mock_user_id', user.id);
          set({ user, isLoading: false });
        } catch {
          // Create new user
          const { user } = await api.createUser({ name });
          localStorage.setItem('mock_user_id', user.id);
          set({ user, isLoading: false });
        }
      },
    }),
    {
      name: 'rango-auth',
      partialize: (state) => ({ user: state.user }),
    }
  )
);

